import React from 'react'
import styled from '@emotion/styled'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Currency, toMoney } from '@mobi/utils/money'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import type { BetSlipItem, FobBetReceiptResponse, MultiInvestment } from '@mobi/betslip/types'
import { getMaxAppliedBonusForMulti } from '@mobi/betslip/helpers/getMaxAppliedBonusForMulti'
import { useBonusAppliedItems } from '@mobi/betslip/hooks/useBonusAppliedItems'
import { calculateTotalBonusesOnItems } from '@mobi/betslip/helpers/calculateTotalBonusesOnItems'
import {
  calculateEstReturn,
  calculateReceiptReturn,
  calculateTotalStake,
  calculateTotalStakeReceipt,
} from '@mobi/betslip/helpers/calculator/combined'
import {
  getBetsToPlace,
  getBetsInMulti,
  isValidMulti,
  hasBeenPlaced,
} from '@mobi/betslip/helpers/state'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'
import { filterSingleItemsForWorkflow } from '@mobi/betslip/Components/Singles/helpers/filterSingleItemsForWorkflow'
import {
  selectBetSlipItems,
  selectMultiBetError,
  selectMultiInvestment,
} from '@mobi/betslip/Store/Bets/selectors'
import { useBetsPlacedSuccessToast } from './hooks/useBetsPlacedSuccessToast'
import { InsufficientFundsError } from './Components/InsufficientFundsError'
import { ApiErrorMessage } from './Components/ApiErrorMessage'

export const SummarySection: React.FC = () => {
  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const items = useAppSelector(selectBetSlipItems)
  const multiInvestment = useAppSelector(selectMultiInvestment)
  const multiBetError = useAppSelector(selectMultiBetError)
  const multiReceipt = useAppSelector(state => state.betslip.bets.multiReceipt)

  useBetsPlacedSuccessToast(workflowStatus)

  const isBetsPlacedView = workflowStatus === 'bets-placed'
  const singlesForWorkflow = filterSingleItemsForWorkflow(items, workflowStatus)
  const singlesWithReceipt = isBetsPlacedView ? singlesForWorkflow.filter(hasBeenPlaced) : []

  const { betSlipItems: bonusUpdatedItems, betSlipMultiInvestment: bonusMultiInvestment } =
    useBonusAppliedItems(singlesForWorkflow)

  const bonusAmount = isBetsPlacedView
    ? calculateBonusAmountOnReceipt({ singlesWithReceipt, multiReceipt })
    : calculateBonusAmountOnUnplaced({ bonusMultiInvestment, bonusUpdatedItems, multiInvestment })

  const totalCost = isBetsPlacedView
    ? calculateTotalStakeReceipt(singlesWithReceipt, multiReceipt)
    : calculateTotalStake(items, multiInvestment, multiBetError)
  const totalCostAfterBonus = new Currency(totalCost).subtract(bonusAmount)

  const potentialReturns = isBetsPlacedView
    ? calculateReceiptReturn(singlesWithReceipt, multiReceipt)
    : calculatePotentialReturnOnItems({ singlesForWorkflow, allItems: items, multiInvestment })

  return (
    <WrapperStyled data-testid='summary-section'>
      <InsufficientFundsError
        singleItems={singlesForWorkflow}
        finalCost={totalCostAfterBonus.value}
      />

      <ApiErrorMessage />

      {workflowStatus !== 'ready' && (
        <div>
          <span>Total Cost</span>
          <span>{totalCostAfterBonus.format()}</span>
        </div>
      )}

      {bonusAmount.value > 0 && (
        <div>
          <span>Bonus</span>
          <span>{bonusAmount.format()}</span>
        </div>
      )}

      {Number.isFinite(potentialReturns) && potentialReturns > 0 && (
        <div>
          <span>Potential Returns</span>
          <span>{toMoney(potentialReturns)}</span>
        </div>
      )}
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function calculatePotentialReturnOnItems({
  singlesForWorkflow,
  allItems,
  multiInvestment,
}: {
  singlesForWorkflow: BetSlipItem[]
  allItems: BetSlipItem[]
  multiInvestment: MultiInvestment
}) {
  const multiItems = getBetsInMulti(allItems)

  return calculateEstReturn(
    getBetsToPlace(singlesForWorkflow),
    isValidMulti(multiInvestment, null, multiItems) ? multiItems : [],
    multiInvestment
  )
}

function calculateBonusAmountOnReceipt({
  singlesWithReceipt,
  multiReceipt,
}: {
  singlesWithReceipt: BetSlipItem[]
  multiReceipt?: FobBetReceiptResponse
}): Currency {
  const singlesBonusAmountUsed = singlesWithReceipt.reduce(
    (total, item) => (item.receipt ? total.add(item.receipt.bonusBet) : total),
    new Currency(0)
  )
  const multiBonusAmountUsed = multiReceipt ? multiReceipt.bonusBet : 0

  return new Currency(singlesBonusAmountUsed).add(multiBonusAmountUsed)
}

function calculateBonusAmountOnUnplaced({
  bonusMultiInvestment,
  bonusUpdatedItems,
  multiInvestment,
}: {
  bonusUpdatedItems: BetSlipItem[]
  multiInvestment: MultiInvestment
  bonusMultiInvestment: MultiInvestment
}): Currency {
  const { finalBonusBet, finalBonusCash } = calculateTotalBonusesOnItems(bonusUpdatedItems)
  const maxAppliedBonusForMulti = getMaxAppliedBonusForMulti(multiInvestment, bonusMultiInvestment)

  return new Currency(0).add(finalBonusBet).add(finalBonusCash).add(maxAppliedBonusForMulti)
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,

  '&:not(:empty)': { paddingBottom: spacing.md },

  '> div': {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.regular,

    '> span': {
      color: colors.white,
    },

    '> span:last-of-type': {
      fontWeight: font.weight.semibold,
    },
  },
})
